import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartFrame, { TOOLTIP_STYLE } from '../dashboard/views/ChartFrame';
import { EmptyState } from '../dashboard/states';
import { FAILURE_CATEGORY_LABELS, fmtDay, fmtMoney, fmtNumber, fmtPct } from './format';

const COLORS = {
  sent: '#6366f1',
  failed: '#f43f5e',
  pending: '#a1a1aa',
  dryRun: '#38bdf8',
  replies: '#f59e0b',
  optOuts: '#9f1239',
  funnel: ['#312e81', '#4338ca', '#6366f1', '#818cf8', '#f43f5e'],
  conversion: '#14b8a6',
};

const axisTick = { fontSize: 10, fill: '#71717a' };

function Card({ children, className = '' }) {
  return <div className={`rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm ${className}`}>{children}</div>;
}

// ---------------------------------------------------------------------------
// 0. Experiment: conversion by arm (control vs treatment) and by touch pattern
// ---------------------------------------------------------------------------
const ARM_LABELS = {
  control: 'Control · d+2..4, noon PT',
  treatment: 'Treatment · d+5..7, 10 AM local + reminder',
  default: 'Default policy',
  legacy: 'Before experiment',
};
const TOUCH_LABELS = {
  pre_arrival_only: 'First text only',
  'pre_arrival+reminder': 'First text + reminder',
};

function BreakdownRows({ rows, labels, keyField }) {
  const max = Math.max(1, ...rows.map((r) => r.conversionPct || 0));
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r[keyField]}>
          <div className="flex items-center justify-between gap-2 text-[11.5px]">
            <span className="truncate font-medium text-zinc-800">{labels[r[keyField]] || r[keyField]}</span>
            <span className="shrink-0 tabular-nums text-zinc-700">
              <span className="font-semibold text-teal-700">{fmtPct(r.conversionPct)}</span>
              <span className="text-zinc-400"> · {fmtNumber(r.converted)}/{fmtNumber(r.texted)} · {fmtMoney(r.revenue)}</span>
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
            <div className="h-full rounded-full bg-teal-500" style={{ width: `${Math.max(2, ((r.conversionPct || 0) / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ExperimentCard({ conversion, loading }) {
  const byArm = (conversion?.byArm || []).filter((r) => r.texted > 0);
  const byTouch = (conversion?.byTouch || []).filter((r) => r.texted > 0);
  const hasArms = byArm.some((r) => r.arm !== 'legacy');
  return (
    <Card className="h-[320px]">
      <ChartFrame
        title="Send-timing experiment"
        subtitle="Conversion by experiment arm and by number of touches (guest-level)"
        footer={
          conversion && !hasArms
            ? 'Arms appear once the planner has sent its first texts. Legacy = sends from before the experiment.'
            : conversion
            ? 'Arms are assigned per phone number (deterministic 50/50). Read with care below ~300 texted guests per arm.'
            : null
        }
      >
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
        ) : !byArm.length && !byTouch.length ? (
          <EmptyState title="No conversion data" description="No successful sends in range." />
        ) : (
          <div className="grid h-full grid-cols-1 gap-4 overflow-auto laptop:grid-cols-2">
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">By arm</p>
              <BreakdownRows rows={byArm} labels={ARM_LABELS} keyField="arm" />
            </div>
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">By touches</p>
              <BreakdownRows rows={byTouch} labels={TOUCH_LABELS} keyField="touches" />
            </div>
          </div>
        )}
      </ChartFrame>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 1. Daily volume: sent vs failed (stacked bars) + replies/opt-outs (lines)
// ---------------------------------------------------------------------------
export function DailyVolumeChart({ days = [], loading, mode }) {
  const data = useMemo(
    () =>
      days.map((d) => ({
        ...d,
        label: fmtDay(d.day),
        pending: Math.max(0, (d.attempted || 0) - (d.sent || 0) - (d.failed || 0)),
      })),
    [days]
  );
  const has = data.some((d) => d.attempted > 0 || d.replies > 0);
  const totals = data.reduce(
    (acc, d) => ({ sent: acc.sent + (d.sent || 0), failed: acc.failed + (d.failed || 0), replies: acc.replies + (d.replies || 0) }),
    { sent: 0, failed: 0, replies: 0 }
  );

  return (
    <Card className="h-[320px]">
      <ChartFrame
        title="Messages per day"
        subtitle={`${mode === 'dry_run' ? 'Dry-run simulations' : mode === 'all' ? 'Live + dry run' : 'Live sends'} · Pacific days`}
        right={
          <div className="flex items-center gap-1.5">
            <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtNumber(totals.sent)} sent</span>
            <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtNumber(totals.failed)} failed</span>
            <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtNumber(totals.replies)} replies</span>
          </div>
        }
      >
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
        ) : has ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} interval="preserveStartEnd" minTickGap={24} />
              <YAxis yAxisId="left" tick={axisTick} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={axisTick} allowDecimals={false} width={28} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: 'rgba(99,102,241,0.06)' }}
                formatter={(value, name) => [fmtNumber(value), name]}
              />
              <Legend wrapperStyle={{ fontSize: 10.5, paddingTop: 4 }} iconSize={8} />
              <Bar yAxisId="left" dataKey="sent" name="Sent" stackId="a" fill={COLORS.sent} isAnimationActive={false} />
              <Bar yAxisId="left" dataKey="failed" name="Failed" stackId="a" fill={COLORS.failed} isAnimationActive={false} />
              <Bar yAxisId="left" dataKey="pending" name="Unconfirmed" stackId="a" fill={COLORS.pending} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Line yAxisId="right" type="monotone" dataKey="replies" name="Replies" stroke={COLORS.replies} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              <Line yAxisId="right" type="monotone" dataKey="optOuts" name="Opt-outs" stroke={COLORS.optOuts} strokeWidth={1.25} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="No messages in range" description="Widen the date range or switch mode." />
        )}
      </ChartFrame>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. Funnel: exported → cleaned → ready → sent (+ failed)
// ---------------------------------------------------------------------------
export function FunnelChart({ funnel, loading }) {
  const stages = useMemo(() => {
    if (!funnel) return [];
    const base = funnel.exported || 0;
    const rows = [
      { key: 'exported', label: 'Exported', hint: 'Reservations in arrival window', value: funnel.exported || 0 },
      { key: 'cleaned', label: 'Cleaned', hint: 'Valid phone, owner/order filters', value: funnel.cleaned || 0 },
      { key: 'ready', label: 'Ready', hint: 'Not texted before', value: funnel.ready || 0 },
      { key: 'sent', label: 'Sent', hint: 'Accepted by Vonage', value: funnel.sent || 0 },
    ];
    return rows.map((r, i) => ({
      ...r,
      pctOfBase: base > 0 ? (r.value / base) * 100 : 0,
      pctOfPrev: i === 0 ? 100 : rows[i - 1].value > 0 ? (r.value / rows[i - 1].value) * 100 : 0,
      color: COLORS.funnel[i],
    }));
  }, [funnel]);

  const hasRuns = stages.some((s) => s.value > 0);

  return (
    <Card className="h-[320px]">
      <ChartFrame
        title="Pipeline funnel"
        subtitle="Summed over runs in range"
        right={
          funnel?.failed ? (
            <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-rose-700">{fmtNumber(funnel.failed)} failed</span>
          ) : null
        }
        footer={hasRuns ? 'Drop-offs: cleaning removes bad numbers/owners; dedupe removes guests already texted.' : null}
      >
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
        ) : hasRuns ? (
          <ul className="flex h-full flex-col justify-center gap-2.5">
            {stages.map((s, i) => (
              <li key={s.key}>
                <div className="mb-1 flex items-baseline justify-between text-[11px]">
                  <span className="font-medium text-zinc-800">
                    {s.label} <span className="font-normal text-zinc-400">· {s.hint}</span>
                  </span>
                  <span className="tabular-nums text-zinc-600">
                    <span className="font-semibold text-zinc-900">{fmtNumber(s.value)}</span>
                    {i > 0 && s.pctOfPrev <= 100 ? <span className="ml-1.5 text-zinc-400">{fmtPct(s.pctOfPrev, 0)} of prev</span> : null}
                  </span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-zinc-100">
                  <div
                    className="h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none"
                    style={{ width: `${Math.min(100, Math.max(2, s.pctOfBase))}%`, background: s.color }}
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No pipeline runs recorded yet"
            description="Funnel counts appear after the first run with the new pipeline tracking."
          />
        )}
      </ChartFrame>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 3. Conversion by lead day (days between text and arrival)
// ---------------------------------------------------------------------------
export function ConversionChart({ conversion, loading }) {
  const data = useMemo(
    () =>
      (conversion?.byLeadDay || []).map((r) => ({
        ...r,
        label: r.daysUntilArrival === 'unknown' ? 'untracked' : `${r.daysUntilArrival}d before arrival`,
        revenuePerText: r.texted > 0 ? Math.round((r.revenue / r.texted) * 100) / 100 : 0,
      })),
    [conversion]
  );
  const has = data.some((d) => d.texted > 0);
  const onlyUnknown = has && data.every((d) => d.daysUntilArrival === 'unknown');

  return (
    <Card className="h-[320px]">
      <ChartFrame
        title="Conversion by lead time"
        subtitle="Texted guests who placed an order for that stay"
        right={
          conversion ? (
            <div className="flex items-center gap-1.5">
              <span className="rounded-md bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-teal-700">{fmtPct(conversion.conversionPct)} overall</span>
              <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtMoney(conversion.revenue)}</span>
            </div>
          ) : null
        }
        footer={
          conversion
            ? `${fmtNumber(conversion.converted)} of ${fmtNumber(conversion.textedGuests)} texted guests ordered · ${fmtMoney(conversion.revenuePerText)} per text${
                onlyUnknown ? ' · lead-day split appears for runs recorded by the new pipeline' : ''
              }`
            : null
        }
      >
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
        ) : has ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 18, right: 8, bottom: 0, left: -16 }} barCategoryGap="30%">
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} />
              <YAxis yAxisId="left" tick={axisTick} unit="%" domain={[0, (max) => Math.max(1, Math.ceil(max * 1.3 * 10) / 10)]} />
              <YAxis yAxisId="right" orientation="right" tick={axisTick} tickFormatter={(v) => `$${v}`} width={40} domain={[0, 'auto']} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: 'rgba(20,184,166,0.06)' }}
                formatter={(value, name, entry) => {
                  const row = entry?.payload || {};
                  if (name === 'Conversion') return [`${fmtPct(value)} (${fmtNumber(row.converted)} of ${fmtNumber(row.texted)} texted)`, name];
                  if (name === 'Revenue / text') return [fmtMoney(value), name];
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 10.5, paddingTop: 4 }} iconSize={8} />
              <Bar
                yAxisId="left"
                dataKey="conversionPct"
                name="Conversion"
                fill={COLORS.conversion}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
                label={{ position: 'top', fontSize: 10, fill: '#3f3f46', formatter: (v) => fmtPct(v) }}
              />
              <Line yAxisId="right" type="monotone" dataKey="revenuePerText" name="Revenue / text" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="No conversion data" description="Needs live sends in range." />
        )}
      </ChartFrame>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 4. Failure categories (horizontal bars)
// ---------------------------------------------------------------------------
export function FailureCategoriesChart({ failures, loading }) {
  const data = useMemo(() => {
    const rows = [...(failures?.byCategory || [])].map((r) => ({
      key: r.category,
      label: FAILURE_CATEGORY_LABELS[r.category] || r.category,
      count: r.count,
    }));
    (failures?.providerFailures || []).forEach((r) => {
      rows.push({ key: `provider:${r.status}:${r.errorCode}`, label: `Provider ${r.status}${r.errorCode && r.errorCode !== 'none' ? ` (${r.errorCode})` : ''}`, count: r.count });
    });
    return rows.sort((a, b) => b.count - a.count).slice(0, 8);
  }, [failures]);
  const total = data.reduce((s, r) => s + r.count, 0);

  return (
    <Card className="h-[320px]">
      <ChartFrame
        title="Why sends fail"
        subtitle="Pipeline + provider failures in range"
        right={total ? <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-rose-700">{fmtNumber(total)} total</span> : null}
      >
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
        ) : data.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="#f4f4f5" horizontal={false} />
              <XAxis type="number" tick={axisTick} allowDecimals={false} />
              <YAxis type="category" dataKey="label" tick={axisTick} width={150} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(244,63,94,0.06)' }} formatter={(v) => [fmtNumber(v), 'Failures']} />
              <Bar dataKey="count" radius={[0, 3, 3, 0]} isAnimationActive={false} label={{ position: 'right', fontSize: 10, fill: '#71717a' }}>
                {data.map((d, i) => (
                  <Cell key={d.key} fill={i === 0 ? '#e11d48' : '#fb7185'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="No failures" description="Every send in range was accepted." />
        )}
      </ChartFrame>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 5. Segments per day (cost proxy) — compact area chart
// ---------------------------------------------------------------------------
export function SegmentsChart({ days = [], loading }) {
  const data = useMemo(() => days.map((d) => ({ label: fmtDay(d.day), segments: d.segments || 0, avg: d.sent ? Math.round((d.segments / d.sent) * 10) / 10 : 0 })), [days]);
  const total = data.reduce((s, d) => s + d.segments, 0);
  const has = total > 0;
  return (
    <Card className="h-[220px]">
      <ChartFrame
        title="SMS segments per day"
        subtitle="Carrier billing unit · ~3 per guest message"
        right={<span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtNumber(total)} segments</span>}
      >
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-lg bg-zinc-50" />
        ) : has ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="smsSegments" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tick={axisTick} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => [name === 'Segments' ? fmtNumber(v) : v, name]} />
              <Area type="monotone" dataKey="segments" name="Segments" stroke="#6366f1" fill="url(#smsSegments)" strokeWidth={2} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="No segments" />
        )}
      </ChartFrame>
    </Card>
  );
}
